import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { round } from '@mahawi/eshop-common/src/round';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type ICountryCurrencyPrice,
  type ICurrencyPrice,
  type ICurrencyType,
  type IExchangeRate,
  type IProductWarehouse,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import {
  Button,
  Col,
  Descriptions,
  Divider,
  Form,
  InputNumber,
  Modal,
  Row,
  Slider,
  Space,
  Table,
  Typography,
} from 'antd';
import { DescriptionsItemType } from 'antd/es/descriptions';
import { type Store } from 'antd/es/form/interface';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { getCountryString } from '../../fragments/get-country-string';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { withNotification } from '../../notification';
import {
  productRecalculatePrices,
  productSetEndPriceMarginRate,
} from '../../reducers/product/reducer';
import { SPACING } from '../../theme/tokens';
import FormSubmitFooter from '../form-submit-footer';
import StatusIcon from '../status-icon';

interface IForm {
  pricesWithoutTax: { [key: string]: number };
}

const { Text, Title } = Typography;

const ISO_CODE_EUR = 'EUR';

function Prices(): React.ReactElement {
  const dispatch = useAppDispatch();
  const productUUID = useSelector((s: RootState) => s.Product.uuid);
  const productInProcess = useSelector((s: RootState) => s.Product.inProcess);
  const productPrices = useSelector((s: RootState) => s.Product.prices);
  const productUpdatedAt = useSelector((s: RootState) => s.Product.updatedAt);
  const productWarehouses = useSelector(
    (s: RootState) => s.Product.productWarehouses,
  );
  const productEndPriceMarginRate = useSelector(
    (s: RootState) => s.Product.endPriceMarginRate,
  );
  const currencies = useSelector((s: RootState) => s.Currency.currencies);
  const exchangeRates = useSelector((s: RootState) => s.Currency.exchangeRates);

  const [form] = Form.useForm<IForm>();

  const [pricesEls, setPricesEls] = useState<React.ReactElement[]>([]);
  const [pricesWithoutTax, setPricesWithoutTax] = useState<{
    [key: string]: number;
  }>({});
  const [profitItems, setProfitItems] = useState<DescriptionsItemType[]>([]);
  const [currencyTypeEUR, setCurrencyTypeEUR] = useState<
    ICurrencyType | undefined
  >(undefined);
  const [productEndPriceMarginRateSlider, setProductEndPriceMarginRateSlider] =
    useState<number>(productEndPriceMarginRate);
  const [isModalVatOpen, setIsModalVatOpen] = useState<Map<string, boolean>>(
    new Map<string, boolean>(),
  );

  const onFinish = useCallback(
    ({ pricesWithoutTax }): void => {
      const priceWithoutTaxEUR = pricesWithoutTax[ISO_CODE_EUR];

      dispatch(
        productRecalculatePrices(productUUID, ISO_CODE_EUR, priceWithoutTaxEUR),
      );
    },
    [productUUID, dispatch],
  );

  useEffect((): void => {
    const pricesElsUE: React.ReactElement[] = currencies.map(
      (currency: ICurrencyType): React.ReactElement => {
        const exchangeRate: IExchangeRate | undefined = exchangeRates.find(
          ({ isoCode }: IExchangeRate): boolean => isoCode === currency.isoCode,
        );

        const prices: ICountryCurrencyPrice[] =
          productPrices.find(
            (p: ICurrencyPrice): boolean => p.isoCode === currency.isoCode,
          )?.pricesWithTax || [];

        const allValid: boolean =
          prices.every((p: ICountryCurrencyPrice): boolean => p.price > 0) &&
          prices.length > 0;

        const showButton: React.ReactElement = (
          <Space>
            <Text type="secondary">Show VAT prices for countries</Text>
            <StatusIcon ok={allValid} />
          </Space>
        );

        return (
          <Row gutter={SPACING.lg} key={currency.isoCode}>
            <Col span={24}>
              <Space orientation="vertical">
                <Text>{currency.name}</Text>
              </Space>
            </Col>

            <Col span={6}>
              <Form.Item>
                <Space.Compact>
                  {currency.isSymbolBeforePrice && (
                    <Button type="default" style={{ pointerEvents: 'none' }}>
                      {currency.symbol}
                    </Button>
                  )}
                  <Form.Item
                    name={['pricesWithoutTax', currency.isoCode]}
                    noStyle
                  >
                    <InputNumber
                      data-currency-iso-code={currency.isoCode}
                      disabled={
                        currency.isoCode !== ISO_CODE_EUR || productInProcess
                      }
                      precision={currency.decimalPrecision}
                    />
                  </Form.Item>
                  {!currency.isSymbolBeforePrice && (
                    <Button type="default" style={{ pointerEvents: 'none' }}>
                      {currency.symbol}
                    </Button>
                  )}
                </Space.Compact>
              </Form.Item>
            </Col>

            <Col span={6}>
              <Button
                type="default"
                onClick={(): void => {
                  setIsModalVatOpen(
                    new Map<string, boolean>([
                      ...isModalVatOpen,
                      [currency.isoCode, true],
                    ]),
                  );
                }}
                disabled={productInProcess}
              >
                {showButton}
              </Button>

              <Modal
                title={`VAT prices by country for ${currency.name} (${currency.isoCode}).`}
                open={isModalVatOpen.get(currency.isoCode) || false}
                onOk={(): void => {
                  setIsModalVatOpen(
                    new Map<string, boolean>([
                      ...isModalVatOpen,
                      [currency.isoCode, false],
                    ]),
                  );
                }}
                onCancel={(): void => {
                  setIsModalVatOpen(
                    new Map<string, boolean>([
                      ...isModalVatOpen,
                      [currency.isoCode, false],
                    ]),
                  );
                }}
                cancelButtonProps={{ style: { display: 'none' } }}
                mask={{ closable: true }}
              >
                <Table dataSource={prices} pagination={false} size="small">
                  <Table.Column
                    title="Country"
                    dataIndex="isoCode"
                    key="isoCode"
                    render={(countryTypeIsoCode: string): string =>
                      `${getCountryString(countryTypeIsoCode)}`
                    }
                    sorter={(
                      a: ICountryCurrencyPrice,
                      b: ICountryCurrencyPrice,
                    ): number => stringCompare(a.isoCode, b.isoCode)}
                    defaultSortOrder={'ascend'}
                  />
                  <Table.Column
                    title="Price"
                    dataIndex="price"
                    key="price"
                    render={(text: number): string =>
                      `${formatCurrency(text, currency)}`
                    }
                    sorter={(
                      a: ICountryCurrencyPrice,
                      b: ICountryCurrencyPrice,
                    ): number => numberCompare(a.price, b.price)}
                  />
                </Table>
              </Modal>
            </Col>

            <Col span={6}>
              <Space orientation="vertical">
                <Text>{exchangeRate ? exchangeRate.rate : EMPTY_VALUE}</Text>
              </Space>
            </Col>

            <Col span={6}>
              <Space orientation="vertical">
                <Text>{`${currency.increaseRate} %`}</Text>
              </Space>
            </Col>
          </Row>
        );
      },
    );

    setPricesEls(pricesElsUE);
  }, [currencies, exchangeRates, isModalVatOpen, productUpdatedAt]);

  useEffect((): void => {
    const pricesWithoutTaxUE: { [key: string]: number } = {};

    productPrices.forEach((price: ICurrencyPrice): void => {
      pricesWithoutTaxUE[price.isoCode] = price.priceWithoutTax;
    });

    setPricesWithoutTax(pricesWithoutTaxUE);

    form.setFieldsValue({
      pricesWithoutTax: pricesWithoutTaxUE,
    });
  }, [form, productPrices, productUpdatedAt]);

  useEffect((): void => {
    const currencyTypeEURUE: ICurrencyType | undefined = currencies.find(
      (c: ICurrencyType): boolean => c.isoCode === ISO_CODE_EUR,
    );

    if (!currencyTypeEURUE) {
      return;
    }

    setCurrencyTypeEUR(currencyTypeEURUE);
  }, [currencies, productUpdatedAt]);

  useEffect((): void => {
    if (!currencyTypeEUR) {
      return;
    }

    const warehouse: IProductWarehouse | undefined = productWarehouses.find(
      (w: IProductWarehouse): boolean => w.warehouse.isPrimary,
    );

    const productWarehousesRecommended: IProductWarehouse[] = [
      ...productWarehouses,
    ]
      .sort((a: IProductWarehouse, b: IProductWarehouse): number =>
        numberCompare(a.pricesEUR.recommended, b.pricesEUR.recommended),
      )
      .filter(
        (w: IProductWarehouse): boolean => (w.pricesEUR.recommended || 0) > 0,
      );

    const recomemndedPriceEURUE: number =
      productWarehousesRecommended.length === 0
        ? 0
        : productWarehousesRecommended[0].pricesEUR.recommended || 0;

    if (!warehouse || !recomemndedPriceEURUE) {
      return;
    }

    const sellingPriceEURWithoutTax: number | undefined = productPrices.find(
      ({ isoCode }: ICurrencyPrice): boolean => isoCode === ISO_CODE_EUR,
    )?.priceWithoutTax;

    if (!sellingPriceEURWithoutTax) {
      return;
    }

    const sellingPriceUE: number = round(sellingPriceEURWithoutTax, 4);
    const purchasePriceEUUE: number = warehouse?.pricesEUR.purchase || 0;

    let profitUE: number = 0;
    let profitPercentUE: number = 0;

    if (purchasePriceEUUE > 0) {
      profitUE = sellingPriceUE - purchasePriceEUUE;
      profitPercentUE = (profitUE / purchasePriceEUUE) * 100;
    }

    setProfitItems([
      {
        key: 'sellingPrice',
        label: 'Selling',
        children: formatCurrency(sellingPriceUE, currencyTypeEUR),
      },
      {
        key: 'recommendedPrice',
        label: 'Recomemnded',
        children: formatCurrency(recomemndedPriceEURUE, currencyTypeEUR),
      },
      {
        key: 'purchasePrice',
        label: 'Purchase',
        children: formatCurrency(purchasePriceEUUE, currencyTypeEUR),
      },
      {
        key: 'profit',
        label: 'Profit',
        children: `${formatCurrency(profitUE, currencyTypeEUR)} (${profitPercentUE.toFixed(4)} %)`,
      },
    ]);
  }, [productPrices, productWarehouses, productUpdatedAt, currencyTypeEUR]);

  const initialValues: Store = {
    pricesWithoutTax,
  };

  return (
    <>
      <Row>
        <Col xs={24} sm={12}>
          <Form
            form={form}
            onFinish={onFinish}
            layout="vertical"
            initialValues={initialValues}
          >
            <Row gutter={SPACING.lg} key="header">
              <Col span={6}>
                <Text>
                  <Text>End price without tax</Text>
                </Text>
              </Col>

              <Col span={6}>
                <Text>
                  <Text>VAT prices for countries</Text>
                </Text>
              </Col>

              <Col span={6}>
                <Text>
                  <Text>Exchange rate related to EUR</Text>
                </Text>
              </Col>

              <Col span={6}>
                <Text>
                  <Text>Increase rate</Text>
                </Text>
              </Col>
            </Row>

            {pricesEls}

            <FormSubmitFooter
              inProcess={productInProcess}
              updatedAt={productUpdatedAt}
              block
            />
          </Form>
        </Col>

        <Col xs={24} sm={12}>
          <Space orientation="vertical" style={{ width: '100%' }}>
            <Title level={5}>Product end price margin rate</Title>
            <Typography.Paragraph type="secondary">
              The margin rate is used to calculate the end price from the
              purchase price. Check also categories margin rate - lowest is
              applied.
            </Typography.Paragraph>
            <Slider
              min={0.5}
              max={2}
              step={0.005}
              marks={{
                0.5: '50%',
                1: '100%',
                1.5: '150%',
                2: '200%',
              }}
              tooltip={{
                formatter: (value: number): string =>
                  `${Math.floor(value * 1000) / 10}%`,
              }}
              defaultValue={productEndPriceMarginRateSlider}
              disabled={productInProcess}
              onChange={(value: number): void => {
                setProductEndPriceMarginRateSlider(value);
              }}
            />

            <Button
              type="primary"
              disabled={productInProcess}
              loading={productInProcess}
              block
              onClick={(): void => {
                dispatch(
                  productSetEndPriceMarginRate(
                    productUUID,
                    productEndPriceMarginRateSlider,
                  ),
                );
              }}
            >
              Submit
            </Button>
          </Space>

          <Divider />

          <Descriptions
            title="Profit calculation without VAT"
            bordered
            column={4}
            items={profitItems}
          />
        </Col>
      </Row>
    </>
  );
}

export default withNotification(React.memo(Prices));
